// client/src/components/leagues/MatchScoreEntry.js
import { useState, useRef, useMemo, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import PageLayout from '../common/layout/PageLayout'
import CancelMatchModal from '../common/CancelMatchModal'
import MatchCentralHeader from './MatchCentralHeader'
import OpponentInfoCard from './OpponentInfoCard'
import MatchActionsCard from './MatchActionsCard'
import ScoreEntryCard from './ScoreEntryCard'
import OutcomeSelector from './OutcomeSelector'
import ChatDrawer from './ChatDrawer'
import ScorePicker from './ScorePicker'
import toastr from 'toastr'
import './MatchScoreEntry.css'
import ApiMatches from '../../api/ApiMatches'
import ApiChat from '../../api/ApiChat'
import ChatService from '../../services/ChatService'
import services from '../../services'
import { track } from '../../analytics'
import {
  getScoreErrorMessage,
  isValidSetScore,
  validateMatchSets,
} from '../../utils/tennisScoreValidation'
import {
  MATCH_STATUS,
  isCompletedStatus,
  isRetirementStatus,
} from '../../utils/matchStatus'
import {
  MATCH_FORMAT,
  normalizeMatchFormat,
} from '../../constants/matchFormats'
import { getMatchSidePlayers, isPlayerOnMatchSide } from '../../utils/match'
import useAuth from '../../hooks/useAuth'
import { queryKeys } from '../../queryClient'

function formatSideDisplayName(players, fallbackName = 'Player') {
  const names = (players || [])
    .map((player) =>
      `${player?.first_name || ''} ${player?.last_name || ''}`.trim(),
    )
    .filter(Boolean)

  if (names.length > 0) return names.join(' / ')
  return fallbackName
}

export default function MatchScoreEntry() {
  const { leagueId, matchId } = useParams()
  const navigate = useNavigate()
  const { isAdmin, user } = useAuth()
  const queryClient = useQueryClient()

  // Only render Set 1 and Set 2 by default; Set 3 is revealed progressively
  const [sets, setSets] = useState([
    { left: null, right: null },
    { left: null, right: null },
    { left: null, right: null },
  ])
  const [pickerVisible, setPickerVisible] = useState(false)
  const [pickerSetIndex, setPickerSetIndex] = useState(null)
  const [playerNames, setPlayerNames] = useState({
    left: 'Player 1',
    right: 'Player 2',
  })
  const [isHomeOnRight, setIsHomeOnRight] = useState(true)
  const [outcome, setOutcome] = useState(MATCH_STATUS.NORMAL)
  const [validationErrors, setValidationErrors] = useState([])
  const [prioritizedError, setPrioritizedError] = useState(null)
  const [setErrors, setSetErrors] = useState({})
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [matchData, setMatchData] = useState(null)
  const [opponentStats, setOpponentStats] = useState(null)
  const [currentUserId, setCurrentUserId] = useState(null)
  const [isLoadingStats, setIsLoadingStats] = useState(true)
  // Inline chat drawer state
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [chatLoading, setChatLoading] = useState(false)
  const [chatError, setChatError] = useState(null)
  const [chatChannel, setChatChannel] = useState(null)
  const [chatMessages, setChatMessages] = useState([])
  const [chatSending, setChatSending] = useState(false)
  const [chatInput, setChatInput] = useState('')
  const [chatUnsub, setChatUnsub] = useState({ msg: null, err: null })

  // Cancel match modal state
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [isCanceling, setIsCanceling] = useState(false)

  // Ref for Set 3 auto-scroll
  const set3Ref = useRef(null)

  // Derived display state
  const isFinalized = isCompletedStatus(matchData?.status)
  const canEdit = !isFinalized || isAdmin // Allow admins to edit finalized matches

  const matchFormat = normalizeMatchFormat(matchData?.match_format)
  const isProSet = matchFormat === MATCH_FORMAT.PRO_SET_8

  const hasAnyScores = sets.some((s) => s.left !== null || s.right !== null)
  const isRetirement = isRetirementStatus(outcome)
  const canSubmit =
    canEdit && ((hasAnyScores && validationErrors.length === 0) || isRetirement)

  // Get opponent details based on current user (memoized)
  const opponentDetails = useMemo(() => {
    if (!matchData || !currentUserId) return null
    const isCurrentUserHome = isPlayerOnMatchSide(
      matchData,
      currentUserId,
      'home',
    )
    const opponentSide = isCurrentUserHome ? 'away' : 'home'
    const opponentPlayers = getMatchSidePlayers(matchData, opponentSide)
    const hasSingleOpponent = opponentPlayers.length <= 1

    const fallbackName =
      opponentSide === 'away'
        ? `${matchData.away_first_name || ''} ${matchData.away_last_name || ''}`.trim() ||
          'Opponent'
        : `${matchData.home_first_name || ''} ${matchData.home_last_name || ''}`.trim() ||
          'Opponent'

    return {
      name: formatSideDisplayName(opponentPlayers, fallbackName),
      email: hasSingleOpponent
        ? opponentSide === 'away'
          ? matchData.away_email
          : matchData.home_email
        : null,
      phone: hasSingleOpponent
        ? opponentSide === 'away'
          ? matchData.away_phone
          : matchData.home_phone
        : null,
      bio: hasSingleOpponent
        ? opponentSide === 'away'
          ? matchData.away_bio
          : matchData.home_bio
        : null,
    }
  }, [matchData, currentUserId])

  // Handle cancel match functionality
  const handleCancelMatchClick = () => {
    setCancelModalOpen(true)
  }

  const handleConfirmCancel = async () => {
    setIsCanceling(true)
    try {
      const result = await services.match.cancelMatch(matchId)
      if (result.success) {
        toastr.success('Match canceled successfully')
        navigate(-1) // Go back to previous page
      } else {
        toastr.error(result.error || 'Failed to cancel match')
      }
    } catch (error) {
      console.error('Failed to cancel match:', error)
      toastr.error(error.message || 'Failed to cancel match')
    } finally {
      setIsCanceling(false)
      setCancelModalOpen(false)
    }
  }

  const handleCloseModal = () => {
    if (!isCanceling) {
      setCancelModalOpen(false)
    }
  }

  // Helper to determine opponent player id for this match
  const getOpponentPlayerId = () => {
    if (!matchData) return null
    const meId = currentUserId || user?.primary_player_id || null
    if (!meId) return null
    const isOnHomeSide = isPlayerOnMatchSide(matchData, meId, 'home')
    const isOnAwaySide = isPlayerOnMatchSide(matchData, meId, 'away')

    if (isOnHomeSide === isOnAwaySide) {
      return null
    }

    const opponentSide = isOnHomeSide ? 'away' : 'home'
    const opponentPlayers = getMatchSidePlayers(matchData, opponentSide)
    return opponentPlayers.length === 1 ? opponentPlayers[0]?.id || null : null
  }

  // Open DM in a side drawer with opponent
  const openOpponentChat = async () => {
    // Debug info to aid troubleshooting if opponent resolution fails
    try {
      console.log('[MatchCentral] openOpponentChat', {
        leagueId,
        matchId,
        hasMatchData: !!matchData,
        currentUserId,
        authPrimaryPlayerId: user?.primary_player_id,
      })
    } catch {
      // Silently ignore logging errors
    }
    if (!leagueId) {
      toastr.warning('League not found for chat')
      return
    }
    if (!matchData) {
      toastr.warning('Match info not loaded yet')
      return
    }
    setChatError(null)
    setChatLoading(true)
    try {
      // Get the user's division in this league (needed for DM channel)
      const divResp = await services.league.getMyDivision(leagueId)
      if (!divResp?.success || !divResp.data?.division_id) {
        throw new Error('Could not determine your division to start chat')
      }
      const divisionId = divResp.data.division_id
      const mePlayerId =
        divResp.data.player_id || user?.primary_player_id || currentUserId

      // Compute opponent id using reliable mePlayerId
      const isOnHomeSide = isPlayerOnMatchSide(matchData, mePlayerId, 'home')
      const isOnAwaySide = isPlayerOnMatchSide(matchData, mePlayerId, 'away')
      const opponentSide =
        isOnHomeSide && !isOnAwaySide
          ? 'away'
          : isOnAwaySide && !isOnHomeSide
            ? 'home'
            : null

      const opponentPlayers = opponentSide
        ? getMatchSidePlayers(matchData, opponentSide)
        : []
      const opponentId =
        opponentPlayers[0]?.id ||
        (opponentSide === 'away'
          ? matchData.away_player_id
          : matchData.home_player_id)
      if (!opponentId) {
        console.warn('openOpponentChat: opponentId unresolved', {
          matchId,
          mePlayerId,
          matchData,
        })
        toastr.warning('Opponent not available for chat')
        return
      }

      // Create/fetch a direct message channel
      const dmResp = await ApiChat.startDirectMessage(opponentId, divisionId)
      const channel = dmResp?.channel
      if (!channel?.id) throw new Error('Failed to start direct message')
      setChatChannel(channel)

      // Connect to chat, join channel, and load messages
      try {
        await ChatService.connect()
      } catch (e) {
        console.warn('Chat connect failed:', e?.message)
      }
      try {
        ChatService.joinChannel(channel.id)
      } catch {
        // Silently ignore join errors
      }

      try {
        const msgsResp = await ApiChat.getMessages(channel.id)
        setChatMessages(
          Array.isArray(msgsResp?.items)
            ? msgsResp.items
            : Array.isArray(msgsResp?.messages)
              ? msgsResp.messages
              : [],
        )
      } catch (e) {
        console.warn('Failed to load chat messages:', e?.message)
      }

      // Subscribe to live messages for this channel
      let unsubMsg = null
      let unsubErr = null
      try {
        unsubMsg = ChatService.onMessage?.((message) => {
          if (message.channel_id === channel.id) {
            setChatMessages((prev) =>
              prev.find((m) => m.id === message.id) ? prev : [...prev, message],
            )
          }
        })
      } catch {
        // Silently ignore subscription errors
      }
      try {
        unsubErr = ChatService.onError?.((e) => {
          console.warn('Chat error:', e?.message)
          setChatError(e?.message || 'Chat connection lost')
        })
      } catch {
        // Silently ignore error handler subscription errors
      }
      setChatUnsub({ msg: unsubMsg, err: unsubErr })

      setIsChatOpen(true)
    } catch (e) {
      console.error(e)
      setChatError(e?.message || 'Unable to open chat')
      toastr.error(e?.message || 'Unable to open chat')
    } finally {
      setChatLoading(false)
    }
  }

  const closeChat = () => {
    try {
      if (typeof chatUnsub.msg === 'function') chatUnsub.msg()
      if (typeof chatUnsub.err === 'function') chatUnsub.err()
    } catch {
      // Silently ignore unsubscribe errors
    }
    setChatUnsub({ msg: null, err: null })
    setIsChatOpen(false)
    setChatInput('')
    setChatError(null)
  }

  const sendChatMessage = async () => {
    const body = chatInput.trim()
    if (!body || chatSending || !chatChannel?.id) return
    setChatSending(true)
    try {
      // Try WebSocket first
      await ChatService.sendMessage(chatChannel.id, body)
      setChatInput('')
    } catch {
      // Fallback to REST
      try {
        const resp = await ApiChat.sendMessage(chatChannel.id, body)
        setChatMessages((prev) => [...prev, resp.message])
        setChatInput('')
      } catch (apiErr) {
        console.error('Failed to send message:', apiErr)
        toastr.error(apiErr?.message || 'Failed to send message')
      }
    } finally {
      setChatSending(false)
    }
  }

  // Optional: open full chat page focused on this DM
  const openFullChat = () => {
    if (chatChannel?.id) {
      navigate('/chat', { state: { openChannelId: chatChannel.id } })
    } else {
      navigate('/chat')
    }
  }

  // Fetch match and player info
  useEffect(() => {
    async function loadMatchAndPlayers() {
      try {
        const matchResp = await ApiMatches.getMatch(matchId)
        if (matchResp) {
          const match = matchResp
          setMatchData(match)

          const homePlayers = getMatchSidePlayers(match, 'home')
          const awayPlayers = getMatchSidePlayers(match, 'away')

          const homeFallback =
            `${match.home_first_name || ''} ${match.home_last_name || ''}`.trim() ||
            'Home Player'
          const awayFallback =
            `${match.away_first_name || ''} ${match.away_last_name || ''}`.trim() ||
            'Away Player'

          const homeName = formatSideDisplayName(homePlayers, homeFallback)
          const awayName = formatSideDisplayName(awayPlayers, awayFallback)

          let currentUserIdCandidate =
            match.current_user_id || match.user_id || match.userId
          if (!currentUserIdCandidate && user?.primary_player_id) {
            currentUserIdCandidate = user.primary_player_id
          }

          setCurrentUserId(currentUserIdCandidate)
          setPlayerNames({ left: awayName, right: homeName })
          setIsHomeOnRight(
            isPlayerOnMatchSide(match, currentUserIdCandidate, 'home'),
          )
        }
      } catch (err) {
        console.warn('Failed to load match or player info:', err.message)
      }
    }
    loadMatchAndPlayers()
  }, [matchId, user?.primary_player_id])

  // Load opponent statistics
  useEffect(() => {
    async function loadOpponentStats() {
      if (!currentUserId || !matchData) return

      setIsLoadingStats(true)
      try {
        const statsResp = await ApiMatches.getOpponentStats(
          matchId,
          currentUserId,
        )
        if (statsResp && statsResp.stats) {
          setOpponentStats(statsResp.stats)
        }
      } catch (err) {
        console.warn('Failed to load opponent stats:', err.message)
      } finally {
        setIsLoadingStats(false)
      }
    }

    if (currentUserId && matchData) {
      loadOpponentStats()
    }
  }, [matchId, currentUserId, matchData])

  // Fetch scores
  useEffect(() => {
    async function loadScores() {
      try {
        const result = await ApiMatches.getScores(matchId)
        if (result.scores && result.scores.length > 0) {
          const sorted = result.scores.sort(
            (a, b) => a.set_number - b.set_number,
          )
          const loadedSets = sorted.map((s) => ({
            left: s.away_games,
            right: s.home_games,
          }))
          // Always render 3 rows: pad with null sets if fewer were returned
          const padded = [
            ...loadedSets,
            { left: null, right: null },
            { left: null, right: null },
            { left: null, right: null },
          ].slice(0, 3)
          setSets(padded)
        }
      } catch {
        // It's ok if there are no scores yet
      }
    }
    loadScores()
  }, [matchId])

  // Keep outcome in sync with finalized match status if matchData changes (no re-fetch loop)
  useEffect(() => {
    if (isCompletedStatus(matchData?.status)) {
      setOutcome(matchData.status)
    }
  }, [matchData?.status])

  // Validate sets whenever they change
  useEffect(() => {
    const isRetired = isRetirementStatus(outcome)
    // Convert UI format to validation format (always home=right, away=left)
    const setsForValidation = sets.map((s) => ({
      home_games: s.right ?? 0,
      away_games: s.left ?? 0,
    }))
    const validation = validateMatchSets(
      setsForValidation,
      isRetired,
      matchFormat,
    )
    setValidationErrors(validation.errors)
    setPrioritizedError(validation.prioritizedError || null)
    // Check individual set validity for styling
    const newSetErrors = {}
    sets.forEach((set, index) => {
      if (
        (set.left !== null && set.left > 0) ||
        (set.right !== null && set.right > 0)
      ) {
        if (!isValidSetScore(set.right ?? 0, set.left ?? 0, matchFormat)) {
          newSetErrors[index] = getScoreErrorMessage(
            set.right ?? 0,
            set.left ?? 0,
            matchFormat,
          )
        }
      }
    })
    setSetErrors(newSetErrors)
  }, [sets, outcome, matchFormat])

  // Open bottom sheet picker for a specific set
  const openPicker = (setIndex) => {
    if (!canEdit) return
    if (isProSet && setIndex !== 0) return
    setPickerSetIndex(setIndex)
    setPickerVisible(true)
  }

  // Close picker
  const closePicker = () => {
    setPickerVisible(false)
    setPickerSetIndex(null)
  }

  // Clear a set
  const clearSet = () => {
    if (pickerSetIndex === null) return
    setSets((prev) =>
      prev.map((s, idx) =>
        idx === pickerSetIndex ? { left: null, right: null } : s,
      ),
    )
    closePicker()
  }

  // Handle score change from picker
  const handleScoreChange = (setIndex, leftVal, rightVal) => {
    setSets((prev) =>
      prev.map((s, idx) =>
        idx === setIndex ? { left: leftVal, right: rightVal } : s,
      ),
    )
  }

  // Determine which sets should be visible
  const visibleSets = useMemo(() => {
    if (!matchData) {
      return [0]
    }

    if (isProSet) {
      return [0]
    }

    const firstTwo = [0, 1]
    const set1 = sets[0]
    const set2 = sets[1]

    if (
      set1.left !== null &&
      set1.right !== null &&
      set2.left !== null &&
      set2.right !== null
    ) {
      const set1Winner =
        set1.left > set1.right
          ? 'left'
          : set1.right > set1.left
            ? 'right'
            : null
      const set2Winner =
        set2.left > set2.right
          ? 'left'
          : set2.right > set2.left
            ? 'right'
            : null

      if (set1Winner && set2Winner && set1Winner !== set2Winner) {
        return [0, 1, 2]
      }
    }

    return firstTwo
  }, [sets, isProSet, matchData])

  useEffect(() => {
    if (isProSet) {
      setSets((prev) => [
        prev[0] || { left: null, right: null },
        ...prev.slice(1).map(() => ({ left: null, right: null })),
      ])
      if (pickerSetIndex !== null && pickerSetIndex !== 0) {
        closePicker()
      }
    }
  }, [isProSet, pickerSetIndex])

  // Auto-scroll to Set 3 when it becomes visible
  useEffect(() => {
    if (visibleSets.length === 3 && set3Ref.current) {
      // Wait for DOM update and render cycle to complete before scrolling
      // This ensures Set 3 row is fully laid out for smooth scroll calculation
      const scrollTimeout = setTimeout(() => {
        if (typeof set3Ref.current?.scrollIntoView === 'function') {
          set3Ref.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }
      }, 100)

      return () => clearTimeout(scrollTimeout)
    }
  }, [visibleSets.length])

  // Generate match summary
  const matchSummary = useMemo(() => {
    if (isRetirement) {
      if (outcome === MATCH_STATUS.HOME_RETIRED) {
        return `${playerNames.left} def. ${playerNames.right} (retirement)`
      }
      if (outcome === MATCH_STATUS.AWAY_RETIRED) {
        return `${playerNames.right} def. ${playerNames.left} (retirement)`
      }
      if (outcome === MATCH_STATUS.OPPONENT_RETIRED) {
        return isHomeOnRight
          ? `${playerNames.right} def. ${playerNames.left} (retirement)`
          : `${playerNames.left} def. ${playerNames.right} (retirement)`
      }
      if (outcome === MATCH_STATUS.I_RETIRED) {
        return isHomeOnRight
          ? `${playerNames.left} def. ${playerNames.right} (retirement)`
          : `${playerNames.right} def. ${playerNames.left} (retirement)`
      }
    }

    const filledSets = sets.filter((s) => s.left !== null && s.right !== null)

    if (filledSets.length === 0) {
      return 'No scores entered yet'
    }

    // Count set wins
    let leftWins = 0
    let rightWins = 0
    filledSets.forEach((s) => {
      if (s.left > s.right) leftWins++
      else if (s.right > s.left) rightWins++
    })

    const winner =
      leftWins > rightWins
        ? playerNames.left
        : rightWins > leftWins
          ? playerNames.right
          : null
    const winnerSide =
      leftWins > rightWins ? 'left' : rightWins > leftWins ? 'right' : null
    const loser =
      leftWins > rightWins
        ? playerNames.right
        : rightWins > leftWins
          ? playerNames.left
          : null

    if (!winner || !loser) {
      return `Match in progress (${filledSets.map((s) => `${s.left}–${s.right}`).join(', ')})`
    }

    const scoreString = filledSets
      .map((s) =>
        winnerSide === 'right'
          ? `${s.right}–${s.left}`
          : `${s.left}–${s.right}`,
      )
      .join(', ')
    return `${winner} def. ${loser} ${scoreString}`
  }, [sets, outcome, isRetirement, playerNames, isHomeOnRight])

  const handleSubmit = async () => {
    setShowValidationErrors(true)
    const isRetired = isRetirementStatus(outcome)
    const setsForValidation = sets.map((s) => ({
      home_games: s.right ?? 0,
      away_games: s.left ?? 0,
    }))
    const validation = validateMatchSets(
      setsForValidation,
      isRetired,
      matchFormat,
    )
    if (!validation.success) {
      // Show prioritized error message with clear guidance
      if (validation.prioritizedError) {
        toastr.error(
          `${validation.prioritizedError.message}\n${validation.prioritizedError.guidance}`,
          'Score Entry Error',
        )
      } else {
        // Fallback to original behavior if no prioritized error
        const errorMessage = validation.errors.join('\n')
        toastr.error(`Please fix the following issues:\n${errorMessage}`)
      }
      return
    }
    if (validation.warnings.length > 0) {
      validation.warnings.forEach((warning) => {
        toastr.warning(warning)
      })
    }
    try {
      try {
        track('match_score_submitted', {
          match_id: matchId,
          league_id: leagueId,
          outcome: outcome,
          sets_count: sets.filter((s) => s.left !== null || s.right !== null)
            .length,
        })
      } catch (analyticsErr) {
        console.error('Analytics tracking error:', analyticsErr)
      }
      await ApiMatches.submitScore(matchId, {
        sets: sets
          .filter((s) => s.left !== null && s.right !== null)
          .map((s, i) => ({
            set_number: i + 1,
            home_games: s.right,
            away_games: s.left,
          })),
        status: outcome,
      })

      // Invalidate React Query caches after score submission
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.scheduledMatches(),
        }),
        queryClient.invalidateQueries({ queryKey: queryKeys.nextMatch() }),
      ])

      toastr.success('Score submitted!')
      navigate(-1)
    } catch (err) {
      toastr.error(err.message || 'Submission failed')
    }
  }

  return (
    <PageLayout title="Match Central">
      {/* Cancel Match Modal */}
      <CancelMatchModal
        isOpen={cancelModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmCancel}
        isProcessing={isCanceling}
        matchDetails={
          matchData
            ? {
                opponentName: opponentDetails?.name,
                dateTime: matchData.match_datetime
                  ? new Date(matchData.match_datetime).toLocaleString()
                  : null,
              }
            : {}
        }
      />

      <div className="match-score-container">
        {/* Match Header */}
        <MatchCentralHeader
          matchData={matchData}
          leagueId={leagueId}
          isFinalized={isFinalized}
        />

        {/* Opponent Information Card */}
        <OpponentInfoCard
          matchData={matchData}
          currentUserId={currentUserId}
          opponentDetails={opponentDetails}
          opponentStats={opponentStats}
          isLoadingStats={isLoadingStats}
          leagueId={leagueId}
          opponentPlayerId={getOpponentPlayerId()}
        />

        {/* Match Actions Card */}
        <MatchActionsCard
          onCancelClick={handleCancelMatchClick}
          onChatClick={openOpponentChat}
          chatLoading={chatLoading}
          isFinalized={isFinalized}
        />

        {/* Admin override banner - only shown when admin is editing finalized match */}
        {isFinalized && isAdmin && (
          <div
            className="alert alert-warning d-flex align-items-center"
            role="alert"
          >
            <div>
              <strong className="me-2">
                <span aria-hidden="true">⚠️</span> Admin Score Override
              </strong>
              <span className="text-muted">
                You are editing scores for a finalized match. Changes will
                replace existing scores.
              </span>
            </div>
          </div>
        )}

        {/* Finalized banner - only show if not admin editing */}
        {isFinalized && !isAdmin && (
          <div
            className="match-finalized-banner alert alert-success d-flex align-items-center justify-content-between"
            role="status"
          >
            <div>
              <strong className="me-2">
                <span aria-hidden="true">🔒</span> Score finalized
              </strong>
              <span className="text-muted">
                {matchData?.status &&
                  (matchData.status === 'normal'
                    ? 'Completed'
                    : matchData.status)}
              </span>
              {hasAnyScores && (
                <span className="ms-2 fw-semibold">
                  •{' '}
                  {sets
                    .filter((s) => s.left !== null || s.right !== null)
                    .map((s) => `${s.left ?? 0}–${s.right ?? 0}`)
                    .join(' | ')}
                </span>
              )}
              {matchData?.submitted_by_first_name &&
              matchData?.submitted_by_last_name ? (
                <span className="d-block small text-muted mt-1">
                  Submitted by {matchData.submitted_by_first_name}{' '}
                  {matchData.submitted_by_last_name}
                </span>
              ) : (
                <span className="d-block small text-muted mt-1">
                  Submitted (user not recorded)
                </span>
              )}
            </div>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => navigate(-1)}
            >
              Go back
            </button>
          </div>
        )}

        {/* Score Entry Card */}
        <ScoreEntryCard
          sets={sets}
          playerNames={playerNames}
          matchData={matchData}
          visibleSets={visibleSets}
          canEdit={canEdit}
          validationErrors={validationErrors}
          prioritizedError={prioritizedError}
          showValidationErrors={showValidationErrors}
          matchSummary={matchSummary}
          onSetClick={openPicker}
          isFinalized={isFinalized}
          leagueId={leagueId}
          setErrors={setErrors}
          set3Ref={set3Ref}
          matchFormat={matchFormat}
          navigate={navigate}
        />

        {canEdit ? (
          <>
            <OutcomeSelector
              outcome={outcome}
              onChange={setOutcome}
              isAdmin={isAdmin}
            />

            <button
              className="btn btn-success btn-lg w-100 mt-4"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              Submit Score
            </button>
            {!canSubmit &&
              hasAnyScores &&
              validationErrors.length > 0 &&
              (() => {
                // Get prioritized error for better help text
                const isRetired = isRetirementStatus(outcome)
                const setsForValidation = sets.map((s) => ({
                  home_games: s.right,
                  away_games: s.left,
                }))
                const validation = validateMatchSets(
                  setsForValidation,
                  isRetired,
                  matchFormat,
                )

                if (validation.prioritizedError) {
                  return (
                    <div className="text-primary small mt-2" role="note">
                      <span aria-hidden="true">💡</span>{' '}
                      {validation.prioritizedError.guidance}
                    </div>
                  )
                }

                return (
                  <div className="text-danger small mt-2" role="note">
                    Fix the highlighted issues above to enable submission.
                  </div>
                )
              })()}
          </>
        ) : (
          <div className="mt-4 d-grid">
            <button
              className="btn btn-outline-secondary"
              onClick={() => navigate(-1)}
            >
              Close
            </button>
          </div>
        )}
      </div>

      {/* Inline Chat Drawer */}
      <ChatDrawer
        isOpen={isChatOpen}
        onClose={closeChat}
        channel={chatChannel}
        messages={chatMessages}
        loading={chatLoading}
        error={chatError}
        sending={chatSending}
        input={chatInput}
        onInputChange={setChatInput}
        onSend={sendChatMessage}
        onOpenFull={openFullChat}
        currentUserId={currentUserId}
        opponentName={opponentDetails?.name}
      />

      {/* Bottom Sheet Picker for Set Scores */}
      <ScorePicker
        visible={pickerVisible}
        setIndex={pickerSetIndex}
        sets={sets}
        playerNames={playerNames}
        onClose={closePicker}
        onScoreChange={handleScoreChange}
        onClear={clearSet}
        matchFormat={matchFormat}
      />
    </PageLayout>
  )
}
